import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import type { BusinessType, CompanyDocumentKey } from "../../types/vtc";
import {
  COMPANY_BUSINESS_TYPES,
  DOCUMENT_MAX_BYTES,
  REQUIRED_COMPANY_DOCS,
} from "../../data/auth";
import { updateProfile } from "../../lib/company";
import { ApiError, errorText, focusFirstError } from "../../lib/api";
import { convertToCompany } from "../../lib/companyApi";
import { useCompany } from "../../lib/companyContext";
import { cn, omit } from "../../lib/format";
import type { AddressParts } from "../../lib/googlePlaces";
import AddressField from "../../components/ui/AddressField";
import { TextField } from "../../components/ui/FormField";
import DocumentUploadRow from "../../components/auth/DocumentUploadRow";
import { ActionButton, BackLink, Card, PageTitle } from "../../components/company/ui";
import { useT } from "../../i18n";

type Fields = {
  name: string;
  siret: string;
  vat: string;
  address: string;
  city: string;
  postalCode: string;
  region: string;
  country: string;
};
type Errors = Partial<Record<keyof Fields | "businessType" | CompanyDocumentKey, string>>;

const REQUIRED: (keyof Fields)[] = ["name", "siret", "address", "city", "postalCode", "country"];

/** Waits for the profile, then shows the conversion (individual accounts only). */
export default function CompanyConvert() {
  const t = useT();
  const { state } = useCompany();
  if (!state.profileLoaded) return <p className="text-sm text-muted">{t.common.loading}</p>;
  // Already a company (e.g. after converting): nothing to do here.
  if (state.profile.ownerType !== "individual") return <Navigate to="/company/profile" replace />;
  return <ConvertForm />;
}

/** Individual → Company: intro, then company details, business type and documents. */
function ConvertForm() {
  const t = useT();
  const c = t.company.convert;
  const pr = t.company.profile;
  const s = t.auth.companySignup;
  const navigate = useNavigate();
  const { state, run, toast } = useCompany();
  const { profile } = state;

  const [started, setStarted] = useState(false);
  const [fields, setFields] = useState<Fields>(() => ({
    name: "",
    siret: "",
    vat: "",
    // The individual's address is the usual starting point for the head office.
    address: profile.address,
    city: profile.city,
    postalCode: profile.postalCode,
    region: profile.region,
    country: profile.country,
  }));
  const [businessType, setBusinessType] = useState<BusinessType | "">("");
  const [documents, setDocuments] = useState<Partial<Record<CompanyDocumentKey, File>>>({});
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key: keyof Fields, value: string) => {
    setFields((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const fillAddress = (parts: AddressParts) =>
    setFields((f) => ({
      ...f,
      address: parts.street || f.address,
      city: parts.city,
      postalCode: parts.postalCode,
      region: parts.state,
      country: parts.country,
    }));

  const neededDocs = businessType ? REQUIRED_COMPANY_DOCS[businessType] : [];
  const missingDocs = neededDocs.filter((key) => !documents[key]);

  const pickType = (type: BusinessType) => {
    setBusinessType(type);
    // Each type asks for its own documents; keep only the ones still needed.
    setDocuments((d) =>
      Object.fromEntries(Object.entries(d).filter(([k]) => REQUIRED_COMPANY_DOCS[type].includes(k as CompanyDocumentKey))),
    );
    setErrors((e) => ({ ...e, businessType: undefined }));
  };

  const selectDocument = (key: CompanyDocumentKey, file: File) => {
    const error =
      file.size > DOCUMENT_MAX_BYTES ? s.fileTooLarge : /\.(pdf|jpe?g|png)$/i.test(file.name) ? undefined : s.fileType;
    setErrors((e) => ({ ...e, [key]: error }));
    if (!error) setDocuments((d) => ({ ...d, [key]: file }));
  };

  const validate = (): Errors => {
    const e: Errors = {};
    REQUIRED.forEach((k) => {
      if (!fields[k].trim()) e[k] = t.common.required;
    });
    if (fields.siret.trim() && !/^\d{14}$/.test(fields.siret.replace(/\s/g, ""))) e.siret = c.siretInvalid;
    if (!businessType) e.businessType = s.businessTypeRequired;
    missingDocs.forEach((k) => (e[k] = s.docRequired));
    return e;
  };

  const submit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length || !businessType) {
      focusFirstError();
      return;
    }
    setSubmitting(true);
    try {
      const trimmed = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.trim()])) as Fields;
      const { profile: converted, message } = await convertToCompany(
        { ...trimmed, siret: trimmed.siret.replace(/\s/g, ""), businessType },
        documents,
      );
      run(updateProfile, converted);
      toast.success(message);
      navigate("/company/profile");
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setErrors(err.errors as Errors);
        focusFirstError();
      }
      toast.error(errorText(err));
    } finally {
      setSubmitting(false);
    }
  };

  const section = (n: number, title: string) => (
    <h2 className="m-0 mb-3 flex gap-2 text-[15px] font-medium">
      <span className="font-mono text-[13px] text-brand">{String(n).padStart(2, "0")}</span>
      {title}
    </h2>
  );

  return (
    <div className="max-w-180">
      <BackLink to="/company/profile">{c.back}</BackLink>
      <PageTitle className="mt-2.5 mb-1.5">{c.title}</PageTitle>
      <p className="m-0 mb-5 max-w-130 text-[13px] leading-normal text-muted">{c.intro}</p>

      {!started ? (
        <Card className="p-6">
          {c.steps.map((step, i) => (
            <div key={step.title} className="flex gap-3.5 border-t border-[#F0F0F0] py-3.5">
              <span className="font-mono text-[13px] text-brand">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div className="text-sm font-medium">{step.title}</div>
                <div className="mt-0.5 text-[13px] text-muted">{step.text}</div>
              </div>
            </div>
          ))}
          <ActionButton variant="primary" size="lg" className="mt-3 px-5.5" onClick={() => setStarted(true)}>
            {c.start}
          </ActionButton>
        </Card>
      ) : (
        <div className="grid gap-4">
          <Card className="p-6">
            {section(1, c.companyInfo)}
            <div className="grid gap-3.5 sm:grid-cols-2">
              <TextField full label={pr.fields.name} placeholder={s.companyNamePlaceholder} autoComplete="organization" value={fields.name} error={errors.name} onChange={(e) => set("name", e.target.value)} />
              <TextField label={pr.fields.siret} inputMode="numeric" placeholder={c.siretPlaceholder} value={fields.siret} error={errors.siret} onChange={(e) => set("siret", e.target.value)} />
              <TextField label={pr.vatOptional} placeholder={c.vatPlaceholder} value={fields.vat} error={errors.vat} onChange={(e) => set("vat", e.target.value)} />
              <AddressField
                full
                label={c.headOffice}
                placeholder={s.addressPlaceholder}
                autoComplete="street-address"
                value={fields.address}
                error={errors.address}
                onAddressSelect={fillAddress}
                onChange={(e) => set("address", e.target.value)}
              />
              <TextField label={pr.fields.city} placeholder={t.common.ph.city} autoComplete="address-level2" value={fields.city} error={errors.city} onChange={(e) => set("city", e.target.value)} />
              <TextField label={pr.fields.postalCode} placeholder={t.common.ph.postalCode} autoComplete="postal-code" value={fields.postalCode} error={errors.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
              <TextField label={pr.fields.region} placeholder={t.common.ph.region} autoComplete="address-level1" value={fields.region} error={errors.region} onChange={(e) => set("region", e.target.value)} />
              <TextField label={pr.fields.country} placeholder={t.common.ph.country} autoComplete="country-name" value={fields.country} error={errors.country} onChange={(e) => set("country", e.target.value)} />
            </div>
          </Card>

          <Card className="p-6">
            {section(2, c.businessType)}
            <div role="radiogroup" aria-label={c.businessType} className="grid gap-2.5">
              {COMPANY_BUSINESS_TYPES.map((type) => (
                <label
                  key={type}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-card border bg-white px-4 py-3 transition-colors",
                    "has-checked:border-brand has-checked:bg-[#F5F8FE]",
                    errors.businessType ? "border-tone-red-fg" : "border-line-strong",
                  )}
                >
                  <input
                    type="radio"
                    name="businessType"
                    value={type}
                    checked={businessType === type}
                    onChange={() => pickType(type)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">{s.businessTypes[type]}</span>
                    <span className="text-[13px] leading-[1.45] text-muted">{s.businessTypeDescs[type]}</span>
                  </span>
                </label>
              ))}
            </div>
            {errors.businessType && <p className="mt-1.5 mb-0 text-xs text-tone-red-fg">{errors.businessType}</p>}
          </Card>

          <Card className="p-6">
            {section(3, c.documents)}
            {businessType ? (
              <>
                <p className="m-0 mb-3 text-[13px] text-muted">{c.requiredFor(s.businessTypes[businessType])}</p>
                <div className="grid gap-2.5">
                  {neededDocs.map((key) => (
                    <DocumentUploadRow
                      key={key}
                      docKey={key}
                      file={documents[key]}
                      error={errors[key]}
                      onSelect={(file) => selectDocument(key, file)}
                      onRemove={() => setDocuments((d) => omit(d, key))}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className="m-0 text-[13px] text-muted">{c.chooseTypeFirst}</p>
            )}
          </Card>

          <div className="flex flex-wrap items-center gap-2.5">
            <ActionButton variant="primary" size="lg" className="px-5.5" disabled={submitting} onClick={() => void submit()}>
              {submitting ? c.submitting : c.submit}
            </ActionButton>
            <ActionButton variant="white" size="lg" disabled={submitting} onClick={() => navigate("/company/profile")}>
              {c.cancel}
            </ActionButton>
            {businessType && missingDocs.length > 0 && (
              <span className="text-[13px] text-tone-amber-fg">{c.missing(missingDocs.length)}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
